import React, { FunctionComponent, useCallback, useMemo } from 'react'
import classNames from 'classnames'
import { compact } from 'lodash'
import { plural, t, Trans } from '@lingui/macro'
import { Select } from 'front-end/src/components'
import { ItemList } from '../../../../../components/common/list/ItemList'
import IconArrowRight from '../../../../../components/common/icon/IconArrowRight'
import { OnFieldMappingChange } from '../../types/FieldsMappingTypes'
import { ListItem, List } from '@chakra-ui/react'

interface IMappedField {
  idx: number
  samples: string[]
  field?: string
}

interface IListHeaderProps {
  mappedFieldsCount: number
}

const ListHeader: FunctionComponent<IListHeaderProps> = ({
  mappedFieldsCount
}) => {
  const fieldsCaption = useMemo(
    () =>
      plural(mappedFieldsCount, {
        one: 'field',
        other: 'fields'
      }),
    [mappedFieldsCount]
  )

  return (
    <>
      <div className="heading-column">
        <Trans>Example values</Trans>:
      </div>
      <div className="heading-column">
        <span
          className={classNames(
            { 'has-matches': mappedFieldsCount > 0 },
            { 'no-matches': mappedFieldsCount === 0 }
          )}
        >
          <Trans>
            {mappedFieldsCount} {fieldsCaption} matched
          </Trans>
        </span>
      </div>
    </>
  )
}

interface IFieldsMappingListItemProps {
  onChange: OnFieldMappingChange
  item: IMappedField
}

export const FieldsMappingListItem: FunctionComponent<IFieldsMappingListItemProps> =
  ({ onChange, item: { idx, samples, field } }) => {
    const MAPPING_SELECTOR_OPTIONS = React.useMemo(
      () => [
        { value: '', label: t`Select...` },
        ...[
          ['email', t`Email`],
          ['password', t`Password`],
          ['first_name', t`First name`],
          ['last_name', t`Last name`],
          ['display_name', t`Display name`],
          ['employee_id', t`Employee ID`],
          ['account_name', t`Account name`],
          ['position', t`Position`],
          ['area', t`Area`],
          ['department', t`Department`],
          ['location', t`Location`],
          ['custom_1', t`Custom 1`],
          ['custom_2', t`Custom 2`],
          ['custom_3', t`Custom 3`],
          ['group', t`Group(s)`],
          ['phone_number', t`Phone number`],
          ['manager_email', t`Manager email`],
          ['manager_account_name', t`Manager account name`]
        ].map(([id, label]) => ({
          value: id,
          label
        }))
      ],
      []
    )

    const renderedSamples = useMemo(
      () =>
        samples.map((sample, idx) => {
          if (sample.includes(';')) {
            const groups = sample.split(';')

            return (
              <>
                <Trans>List of values:</Trans>
                <List>
                  {groups.map((g) => (
                    <ListItem
                      key={idx}
                      style={{ listStyle: 'inside', paddingLeft: '12px' }}
                    >
                      {g}
                    </ListItem>
                  ))}
                </List>
              </>
            )
          }

          return <li key={idx}>{sample}</li>
        }),
      [samples]
    )

    const handleChange = useCallback(
      (newMappedField) => {
        onChange(idx, newMappedField.value || undefined)
      },
      [onChange, idx]
    )

    return (
      <div className="BulkUsersImportPage__mapping-item">
        <div className="mapping-item-samples">
          <ul className="samples-list">{renderedSamples}</ul>
        </div>
        <IconArrowRight className="mapping-item-delimiter" />
        <div className="mapping-item-selector">
          <Select
            onChange={handleChange}
            options={MAPPING_SELECTOR_OPTIONS}
            value={MAPPING_SELECTOR_OPTIONS.find(({ value }) => {
              return value === field
            })}
            fullWidth
          />
        </div>
      </div>
    )
  }

export interface IFieldsMappingListProps {
  fieldsMap: string[]
  onChange: OnFieldMappingChange
  samples: string[][]
}

export const FieldMappingList: FunctionComponent<IFieldsMappingListProps> = ({
  fieldsMap,
  onChange,
  samples
}) => {
  const items = fieldsMap.map((field, idx) => ({
    idx,
    field,
    samples: samples[idx]
  }))

  const mappedCount = compact(fieldsMap).length

  return (
    <ItemList
      showFooter={false}
      items={items}
      itemsCount={items.length}
      keyPath="idx"
      header={<ListHeader mappedFieldsCount={mappedCount} />}
      renderItem={(item) => (
        <FieldsMappingListItem item={item} onChange={onChange} />
      )}
    />
  )
}
